//#region imports
import React from 'react';
//#endregion

//#region styles

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#1f2937',
};

const labelCellStyle = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

// Shared column widths so every summary table in the SLA Summary Report lines
// up vertically regardless of its content.
const SUMMARY_COLGROUP = (
    <colgroup>
        <col style={{ width: '40%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '14%' }} />
    </colgroup>
);

//#endregion

//#region helpers
const parseTarget = (target) => {
    if (!target) return 0;
    return parseFloat(target.replace('%', ''));
};

const isNoDataRow = (row) => row.passReason === 'No Boarding';

/** Recompute KPI counts from rawData excluding skip rows.
 * Falls back to server-computed counts when rawData isn't present (e.g. when
 * the summary view requests the report with hideBreakdown=true and the server
 * has already recomputed the skip-excluded totals for us). */
const recomputeKpiFromRawData = (kpi) => {
    if (!kpi) return kpi;
    if (!Array.isArray(kpi.rawData)) {
        const totalPass = kpi.totalPass ?? kpi.passCount ?? kpi.pass ?? 0;
        const total = kpi.total ?? kpi.totalAssisted ?? 0;
        const score = kpi.score ?? (total > 0 ? Math.round((totalPass / total) * 100) : 0);
        return { ...kpi, totalPass, total, score };
    }
    const filtered = kpi.rawData.filter((row) => !isNoDataRow(row));
    const totalPass = filtered.filter((row) => row.pass === true).length;
    const total = filtered.length;
    const score = total > 0 ? Math.round((totalPass / total) * 100) : 0;
    return { ...kpi, totalPass, total, score };
};

/** Build kpis with optional exclusion of skip rows. */
const getKpisForReport = (report, includeNoData) => {
    const kpis = report?.kpis || {};
    if (includeNoData) return kpis;
    const result = {};
    Object.keys(kpis).forEach((key) => {
        result[key] = recomputeKpiFromRawData(kpis[key]);
    });
    return result;
};

/** Build overall summary kpis aggregating across daily reports, optionally excluding skip rows. */
export const getOverallKpis = (dailyReports, overallSummary, includeNoData) => {
    if (includeNoData || !dailyReports?.length) return overallSummary?.kpis || null;
    const kpiKeys = Object.keys(dailyReports[0]?.kpis || {});
    if (!kpiKeys.length) return null;
    const kpis = {};
    kpiKeys.forEach((key) => {
        let totalPass = 0;
        let total = 0;
        let title = '';
        let minutes = 0;
        let target = '';
        dailyReports.forEach((report) => {
            const recalc = recomputeKpiFromRawData(report.kpis?.[key]);
            totalPass += recalc.totalPass || 0;
            total += recalc.total || 0;
            if (!title && recalc.title) {
                title = recalc.title;
                minutes = recalc.minutes;
                target = recalc.target;
            }
        });
        // const score = total > 0 ? Math.ceil((totalPass / total) * 100) : 0;
        const score = total > 0 ? Math.round((totalPass / total) * 100) : 0;
        kpis[key] = { title, minutes, target, totalPass, total, score };
    });
    return kpis;
};

//#endregion

//#region components table
const KpiRow = ({ kpi }) => {
    const targetNum = parseTarget(kpi.target);
    const meetsTarget = kpi.score >= targetNum;
    // summary uses totalPass, daily uses pass
    const passCount = kpi.totalPass ?? kpi.pass;

    return (
        <tr>
            <td style={labelCellStyle}>{kpi.title}</td>
            <td style={cellStyle}>{kpi.minutes} min</td>
            <td style={cellStyle}>{kpi.target}</td>
            <td style={cellStyle}>{passCount}</td>
            <td style={cellStyle}>{kpi.total}</td>
            <td style={{
                ...cellStyle,
                fontWeight: 600,
                backgroundColor: kpi.total === 0 ? '#f9fafb' : meetsTarget ? '#dcfce7' : '#fee2e2',
                color: kpi.total === 0 ? '#6b7280' : meetsTarget ? '#166534' : '#991b1b',
            }}>
                {kpi.total === 0 ? '-' : `${kpi.score}%`}
            </td>
        </tr>
    );
};

const KpiTable = ({ title, kpis }) => {
    const kpiKeys = kpis ? Object.keys(kpis) : [];
    if (!kpiKeys.length) return null;

    return (
        <div style={{ marginBottom: '24px' }}>
            <table style={tableStyle}>
                {SUMMARY_COLGROUP}
                <thead>
                    <tr>
                        <th style={headerStyle}>{title}</th>
                        <th style={headerStyle}>Minutes</th>
                        <th style={headerStyle}>Target</th>
                        <th style={headerStyle}>Total Pass</th>
                        <th style={headerStyle}>Total</th>
                        <th style={headerStyle}>Score</th>
                    </tr>
                </thead>
                <tbody>
                    {kpiKeys.map((key) => (
                        <KpiRow key={key} kpi={kpis[key]} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};
//#endregion

const BoardingSlaSummary = ({ dailyReports, overallSummary, includeNoData = false, hideDailyBreakdown = false }) => {
    if (!dailyReports?.length) return <p>No boarding SLA data available.</p>;

    const overallKpis = getOverallKpis(dailyReports, overallSummary, includeNoData);
    const isMultiDay = (dailyReports?.length ?? 0) > 1;
    const showOverall = overallKpis && (hideDailyBreakdown || isMultiDay);

    return (
        <div>
            {showOverall && (
                <KpiTable title="Overall Summary" kpis={overallKpis} />
            )}
            {!hideDailyBreakdown && dailyReports.map((report) => (
                <KpiTable key={report.date} title={report.date} kpis={getKpisForReport(report, includeNoData)} />
            ))}
        </div>
    );
};

export default BoardingSlaSummary;
